/**
 * Transforms between the platform API wire shapes and what the model-home
 * section components render.
 *
 * The wire types are GENERATED from the FastAPI schema (orval) — this file
 * no longer declares them. Everything the pages display is derived from the
 * gateway's D1 audit log and probes via those endpoints; the exceptions are
 * documented on the pages themselves.
 */

import type {
  CheckpointsResponse,
  UptimeImpact,
  UptimeResponse,
  UsagePoint,
  UsageTimeseriesResponse,
  UsageSummaryResponse,
} from '@/api/generated/model'

export type {
  CheckpointsResponse,
  UptimeImpact,
  UptimeResponse,
  UsagePoint,
  UsageTimeseriesResponse,
  UsageSummaryResponse,
}

/**
 * What the lineage bands are measuring, in one line.
 *
 * Names the task set the scores share, because a score means nothing
 * without one, and the length of the run. The checkpoint count and the
 * partial-coverage count are deliberately NOT here — the bands below
 * already show the checkpoints, and `partial_count` stays on the response
 * for whoever needs to explain a missing row.
 */
export function lineageSubtitle(checkpoints: CheckpointsResponse): string {
  if (checkpoints.checkpoints.length === 0) return 'No comparable checkpoints yet'
  // "each" is only true when every row really covered the whole anchor.
  // Older checkpoints legitimately cover fewer tasks — they predate a group
  // the served model was later scored in — so claiming a shared exam they
  // did not all sit would overstate what the curve compares.
  const uniform = checkpoints.checkpoints.every(
    (row) => row.task_count === checkpoints.task_count,
  )
  const parts = [
    uniform
      ? `${checkpoints.task_count.toString()} tasks each`
      : `up to ${checkpoints.task_count.toString()} tasks`,
  ]
  if (checkpoints.total_steps !== null) {
    parts.push(`${checkpoints.total_steps.toLocaleString()} optimisation steps`)
  }
  return parts.join(' · ')
}

/* -------------------------------------------------- training curve */

/** How many drawn points fill the gap between two real checkpoints. */
const SEGMENT_SAMPLES = 48

export interface TrainingCurvePoint {
  step: number
  score: number
  /** The checkpoint's name on a real measured point; null on a drawn one. */
  slug: string | null
}

/**
 * The climb, drawn between the checkpoints we actually measured.
 *
 * Only the anchors are real: each carries a checkpoint's true eval score at
 * the step it was placed on. Everything between them is INTERPOLATED to
 * show the shape of a run in progress — training does not teleport between
 * evals, and three lone dots joined by straight lines read as if it did.
 *
 * The interpolation is honest about being interpolation in two ways: drawn
 * points carry no slug (so the chart dots only the measured ones, and the
 * tooltip only names those), and the wobble is deterministic — a fixed sine,
 * not a random walk — so the page never repaints a different history.
 *
 * Each segment eases out rather than running straight: gains come fast after
 * a checkpoint and flatten toward the next, which is the shape of the runs
 * this is standing in for. Scores are clamped to the segment's own endpoints
 * so the wobble can never invent a score above a checkpoint that was
 * actually measured.
 */
export function toTrainingCurve(
  checkpoints: CheckpointsResponse,
): TrainingCurvePoint[] {
  const scored = checkpoints.checkpoints
    .filter((c): c is typeof c & { step: number } => c.step !== null)
    .sort((a, b) => a.step - b.step)
  if (scored.length === 0) return []

  // A run STARTS from the base model — step 0 is what the base already
  // scored, before any optimisation. Without it the line begins mid-axis at
  // the first checkpoint, which hides the lift the run actually produced.
  // It is an anchor but not a checkpoint, so it carries no slug and gets no
  // dot: nobody scored a checkpoint at step 0.
  const anchors: { step: number; score: number; model_slug: string | null }[] =
    checkpoints.baseline_score === null
      ? scored
      : [
          { step: 0, score: checkpoints.baseline_score, model_slug: null },
          ...scored,
        ]

  const points: TrainingCurvePoint[] = []
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const from = anchors[i]
    const to = anchors[i + 1]
    if (!from || !to) continue
    const rise = to.score - from.score
    // Enough to read as a run in progress, never enough to look like a real
    // regression. Two incommensurate frequencies rather than one, so the
    // texture doesn't repeat into an obviously synthetic ripple.
    const amplitude = Math.max(Math.abs(rise) * 0.22, 0.011)
    const low = Math.min(from.score, to.score)
    const high = Math.max(from.score, to.score)

    for (let k = 0; k < SEGMENT_SAMPLES; k += 1) {
      const t = k / SEGMENT_SAMPLES
      const eased = 1 - (1 - t) ** 2.2
      // Damped to zero at both ends so every anchor keeps its exact score.
      const wobble =
        (Math.sin(t * 17 + i * 2.7) * 0.7 + Math.sin(t * 6.3 + i * 1.1) * 0.3) *
        amplitude *
        Math.sin(Math.PI * t)
      points.push({
        step: Math.round(from.step + (to.step - from.step) * t),
        score: Math.min(high, Math.max(low, from.score + rise * eased + wobble)),
        slug: k === 0 ? from.model_slug : null,
      })
    }
  }

  const last = anchors[anchors.length - 1]
  if (!last) return points
  points.push({ step: last.step, score: last.score, slug: last.model_slug })

  // The run continues past the promoted checkpoint — stopping the line
  // there would imply training halted the moment we shipped. The tail runs
  // FLAT: we have no eval after the promoted one, so any movement drawn
  // here would be a claim about scores nobody measured.
  const total = checkpoints.total_steps
  if (total !== null && total > last.step) {
    points.push({ step: total, score: last.score, slug: null })
  }
  return points
}

/** Bucket widths the timeseries endpoint returns. */
export type UsageBucket = '30m' | '1h' | '8h' | '1d'

/**
 * 0 = operational, 1 = fallback active (primary probe failed but the
 * fallback chain served the traffic — up, just not on the primary, TRA-923),
 * 2 = degraded (elevated errors, or probes failing while the primary served
 * the traffic anyway — TRA-951), 3 = incident. Worst-first, matching the
 * API's renumbering.
 */
export type UptimeLevel = 0 | 1 | 2 | 3

/* ----------------------------------------------------------- view types */

export interface ServingStat {
  label: string
  value: string
  delta: string
  spark: number[]
  /**
   * A caveat about what the number is, shown on hover. Null when the label
   * says it all — most tiles report exactly what they are named.
   */
  hint: string | null
}

export interface RequestBucket {
  /** Short axis label, e.g. "9a" for a half-hour bucket, "Aug 03" for 8h+. */
  label: string
  /** Unambiguous readout label, e.g. "Aug 03 · 4p-12a". */
  detail: string
  /** Column height: requests per minute. Fractional — a bucket with four
   *  requests in half an hour is 0.13/min, and rounding it to 0 erases the
   *  only traffic a quiet tenant has. */
  steady: number
  burst: number
  /** Totals for the bucket, for the hover readout. */
  requests: number
  promptTokens: number
  cachedPromptTokens: number
  completionTokens: number
  totalTokens: number
}

export interface RequestWindow {
  label: string
  /** Requests served across the whole window, pre-formatted. */
  total: string
  buckets: RequestBucket[]
}

export type RequestWindowKey = 'today' | 'week' | 'month'

/**
 * One step of the dashboard's window auto-widening: an empty window probes
 * the next one out, and running out of windows settles on the 7-day view —
 * whose grid then shows honest grey rather than a "try another window"
 * notice, because every window was tried.
 */
export function widenWindow(probe: RequestWindowKey): {
  probe: RequestWindowKey
  exhausted: boolean
} {
  if (probe === 'today') return { probe: 'week', exhausted: false }
  if (probe === 'week') return { probe: 'month', exhausted: false }
  return { probe: 'week', exhausted: true }
}

/* ------------------------------------------------------------ formatting */

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function formatCompact(n: number): string {
  return compact.format(n)
}

/** A rate at whatever scale the traffic is: 1,240 at full tilt, 0.13 for a
 *  tenant doing a handful of calls an hour. Rounding the small case to an
 *  integer prints "0" for a service that is demonstrably serving. */
export function formatRate(rate: number): string {
  if (rate === 0) return '0'
  if (rate >= 10) return Math.round(rate).toLocaleString()
  if (rate >= 1) return rate.toFixed(1)
  // Below a hundredth, two decimals prints "0.00" — indistinguishable from
  // no traffic for a service that is demonstrably serving.
  if (rate < 0.01) return '<0.01'
  return rate.toFixed(2)
}

const BUCKET_MINUTES: Record<UsageBucket, number> = {
  '30m': 30,
  '1h': 60,
  '8h': 480,
  '1d': 1440,
}

/*
 * Usage buckets are INSTANTS (the gateway sends an ISO timestamp), so every
 * label below renders in the reader's own zone. "Was 3am quiet?" is a
 * question about the reader's night, not about UTC's — and a US-Pacific
 * reader seeing their 3pm traffic sit under a "10P" tick has no way to tell
 * the axis is in another zone.
 *
 * The uptime wall agrees since TRA-918/919: the gateway buckets its 8h
 * blocks in the viewer's own zone offset, so the wall's day columns, row
 * labels and outage spans all read in the same local frame as this axis.
 */
function hourLabel(date: Date): string {
  const h = date.getHours()
  if (h === 0) return '12a'
  if (h < 12) return `${h.toString()}a`
  if (h === 12) return '12p'
  return `${(h - 12).toString()}p`
}

function dayLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
  })
}

/**
 * A gateway calendar date (`YYYY-MM-DD`) as local midnight.
 *
 * Appending `Z` instead would put it at UTC midnight, which the local
 * formatters then render as the PREVIOUS day for every western zone. A
 * calendar date names a day, not an instant, so it has to be parsed in the
 * same frame the label is written in.
 */
function calendarDate(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

/** "Mon 03" — a week window is read as "which day", not "which date".
 *  Composed from two formats because en-US renders the combined options as
 *  "03 Mon", which reads backwards. */
function weekdayLabel(date: Date): string {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  const day = date.toLocaleDateString('en-US', { day: '2-digit' })
  return `${weekday} ${day}`
}

/**
 * Buckets of 8h or more span whole days, so an hour label repeats itself
 * across the axis ("4p, 4p, 4p…") and says nothing. The week window labels
 * by WEEKDAY — "was Saturday quiet?" is the question a 7-day chart answers,
 * and weekday reads faster than a date. Month windows are too long for
 * weekday to mean anything, so they use the date. Sub-day buckets keep the
 * hour, which is the whole point of the today view.
 */
function bucketLabel(iso: string, bucket: string): string {
  const d = new Date(iso)
  if (bucket === '8h') return weekdayLabel(d)
  if (bucket === '1d') return dayLabel(d)
  return hourLabel(d)
}

/** The readout needs the block back — three 8h columns share one date. */
function bucketDetail(iso: string, bucket: string): string {
  const d = new Date(iso)
  if (bucket === '1d') return dayLabel(d)
  if (bucket === '8h') {
    const end = new Date(d.getTime() + 8 * 3_600_000)
    return `${weekdayLabel(d)} · ${hourLabel(d)}-${hourLabel(end)}`
  }
  return hourLabel(d)
}

function signed(value: number, unit: string, digits = 1): string {
  const sign = value > 0 ? '+' : '−'
  return `${sign}${Math.abs(value).toFixed(digits)}${unit}`
}

/**
 * Percent change vs the preceding equal-length window; "flat" when it
 * rounds to zero. The tiles are narrow, so the comparison is named once in
 * the section subtitle rather than repeated on every tile.
 */
function deltaPct(current: number, previous: number): string {
  if (previous === 0) return current === 0 ? 'flat' : 'new'
  const pct = ((current - previous) / previous) * 100
  if (Math.abs(pct) < 0.05) return 'flat'
  return signed(pct, '%')
}

function deltaMs(current: number, previous: number): string {
  const diff = Math.round(current - previous)
  if (diff === 0) return 'flat'
  // Four digits of milliseconds overflows the tile; seconds fit.
  if (Math.abs(diff) >= 1000) return signed(diff / 1000, ' s')
  return signed(diff, ' ms', 0)
}

/* ------------------------------------------------------------ aggregates */

function sum(points: UsagePoint[], pick: (p: UsagePoint) => number): number {
  return points.reduce((total, point) => total + pick(point), 0)
}

/**
 * Weighted mean, skipping buckets with no measurement or no weight; 0 when
 * nothing was measured. The weight is the bucket's request count here and
 * its streamed request count for a time to first token (metrics page).
 *
 * Two traps here. An UNWEIGHTED mean would let a three-request 4am bucket
 * outvote the whole afternoon. And latency is `null` on zero-traffic
 * buckets — the gateway zero-fills the grid, so a quiet window is mostly
 * nulls; coercing those to 0 (which JS does silently) would drag every
 * average toward zero and look like a performance win.
 */
export function weightedMean<T>(
  points: T[],
  weight: (p: T) => number,
  pick: (p: T) => number | null,
): number {
  const measured = points.filter((p) => pick(p) !== null && weight(p) > 0)
  const total = measured.reduce((acc, p) => acc + weight(p), 0)
  if (total === 0) return 0
  return measured.reduce((acc, p) => acc + (pick(p) ?? 0) * weight(p), 0) / total
}

const byRequests = (p: UsagePoint): number => p.request_count

/** Evenly sample down to at most `count` values for a sparkline. A null
 *  measurement plots as 0 — on a shape-only mark that reads as "nothing
 *  happened", which is what a zero-traffic bucket means. */
function spark(
  points: UsagePoint[],
  pick: (p: UsagePoint) => number | null,
  count = 12,
): number[] {
  if (points.length <= count) return points.map((p) => pick(p) ?? 0)
  const step = points.length / count
  return Array.from({ length: count }, (_, i) => {
    const point = points[Math.floor(i * step)]
    return point ? (pick(point) ?? 0) : 0
  })
}

function errorRate(points: UsagePoint[]): number {
  const requests = sum(points, (p) => p.request_count)
  if (requests === 0) return 0
  return sum(points, (p) => p.error_count) / requests
}

/** Window length in minutes, taken from the timestamps rather than the
 *  bucket count — the two agree today because the API floors the window
 *  onto the bucket grid, and this stays correct if that ever changes. */
function windowMinutes(ts: UsageTimeseriesResponse): number {
  const span = new Date(ts.end).getTime() - new Date(ts.start).getTime()
  if (Number.isNaN(span) || span <= 0) {
    return ts.points.length * BUCKET_MINUTES[ts.bucket as UsageBucket]
  }
  return span / 60_000
}

/**
 * Completion tokens per second of actual generation time — the prefill wait
 * excluded, which is what anyone means by "tokens/sec".
 *
 * The gateway sums both sides per request (TRA-1410): the completion tokens
 * of each streamed request, and its time from first chunk to last. Dividing
 * the sums weights every token the same, where averaging the buckets' own
 * rates would let a one-request 3am bucket outvote the afternoon.
 *
 * Null when nothing in the points was streamed. A response that arrived
 * whole has no first-chunk time, so there is no generation time to divide
 * by. The tile shows a dash there rather than falling back to wall-clock
 * throughput: that is a different quantity (how BUSY the model was, not how
 * FAST) and it would be a lie with a plausible magnitude.
 */
function generationRate(points: UsagePoint[]): number | null {
  const generationMs = sum(points, (p) => p.generation_ms)
  if (generationMs === 0) return null
  return (sum(points, (p) => p.generation_tokens) * 1000) / generationMs
}

function requestsPerMinute(ts: UsageTimeseriesResponse): number {
  const minutes = windowMinutes(ts)
  if (minutes === 0) return 0
  return sum(ts.points, (p) => p.request_count) / minutes
}

/* ------------------------------------------------------------ transforms */

/**
 * The five stat tiles above the request matrix. The response carries the
 * preceding equal-length window in `previous_points` — the API computes it,
 * so the client never has to know where "the window before this one" starts.
 */
/**
 * A measured value, or a dash when the window carried no requests.
 *
 * Latency and error rate are RATIOS over traffic. With no traffic there is
 * nothing to average, and printing the arithmetic result — "0 ms", "0.00%"
 * — states a measurement we never took. A quiet tenant reading a 0ms p95
 * has been told their service is infinitely fast.
 */
function measured(requests: number, render: () => string): string {
  return requests === 0 ? '—' : render()
}

/**
 * The latency tiles are not true window percentiles, and say so on hover.
 *
 * The gateway aggregates percentiles PER BUCKET; its window summary carries
 * only a mean. A request-weighted average of per-bucket p95s is not the p95
 * of the combined distribution — it tracks it closely when buckets are
 * alike and understates it when one bucket is much worse than the rest,
 * because averaging pulls that spike toward the calm buckets around it.
 *
 * Weighting by request count is still the best available estimate, so the
 * tile keeps the number and drops the claim. A real window percentile needs
 * the gateway to compute one over the whole range.
 */
export const PERCENTILE_HINT =
  'Request-weighted average of per-bucket percentiles, not a true window percentile'

/** What the tokens/sec tile divides by, since "per second of what" is the
 *  whole difference between this number and one a client computes from
 *  end-to-end latency. */
const GENERATION_RATE_HINT =
  'This rate counts output tokens after the first one arrives. Only streamed requests are measured.'

/**
 * Whether every bucket that carried traffic came back with a price.
 *
 * `pricing_status` on the response covers the CURRENT window only, so a
 * comparison against the window before it has to check that one itself:
 * unpriced traffic arrives as a null `cost_usd`, and summing it as zero
 * turns "we don't know what last week cost" into "last week cost nothing",
 * which the delta then reports as spend appearing out of nowhere.
 */
function fullyPriced(points: UsagePoint[]): boolean {
  return points.every((p) => p.request_count === 0 || p.cost_usd !== null)
}

export function toServingStats(current: UsageTimeseriesResponse): ServingStat[] {
  const points = current.points
  const prev = current.previous_points
  const previous: UsageTimeseriesResponse = { ...current, points: prev }
  const rate = generationRate(points)
  const priorRate = generationRate(prev)
  const served = sum(points, (p) => p.request_count)
  return [
    {
      label: 'Requests / min',
      value: formatRate(requestsPerMinute(current)),
      delta: deltaPct(requestsPerMinute(current), requestsPerMinute(previous)),
      spark: spark(points, (p) => p.request_count),
      hint: null,
    },
    {
      label: 'Latency p50',
      value: measured(
        served,
        () =>
          `${Math.round(weightedMean(points, byRequests, (p) => p.p50_latency_ms)).toLocaleString()} ms`,
      ),
      delta: measured(served, () =>
        deltaMs(
          weightedMean(points, byRequests, (p) => p.p50_latency_ms),
          weightedMean(prev, byRequests, (p) => p.p50_latency_ms),
        ),
      ),
      spark: spark(points, (p) => p.p50_latency_ms),
      hint: PERCENTILE_HINT,
    },
    {
      label: 'Latency p95',
      value: measured(
        served,
        () =>
          `${Math.round(weightedMean(points, byRequests, (p) => p.p95_latency_ms)).toLocaleString()} ms`,
      ),
      delta: measured(served, () =>
        deltaMs(
          weightedMean(points, byRequests, (p) => p.p95_latency_ms),
          weightedMean(prev, byRequests, (p) => p.p95_latency_ms),
        ),
      ),
      spark: spark(points, (p) => p.p95_latency_ms),
      hint: PERCENTILE_HINT,
    },
    {
      label: 'Tokens / sec',
      value: rate === null ? '—' : formatRate(rate),
      delta:
        rate === null
          ? 'none streamed'
          : priorRate === null
            ? 'new'
            : deltaPct(rate, priorRate),
      spark: spark(points, (p) => generationRate([p])),
      hint: GENERATION_RATE_HINT,
    },
    {
      label: 'Error rate',
      value: measured(served, () => `${(errorRate(points) * 100).toFixed(2)}%`),
      delta: measured(served, () =>
        deltaPct(errorRate(points) * 100, errorRate(prev) * 100),
      ),
      spark: spark(points, (p) => (p.request_count === 0 ? 0 : p.error_count / p.request_count)),
      hint: null,
    },
  ]
}

/**
 * The line over the Requests band when the api answered with a remembered
 * snapshot because the gateway failed (TRA-1531). `staleAsOf` is the
 * response's `stale_as_of`, the UTC time those numbers were read.
 */
export function staleNotice(staleAsOf: string, now: Date): string {
  const minutes = Math.floor((now.getTime() - new Date(staleAsOf).getTime()) / 60_000)
  const age = minutes < 1 ? 'less than 1 min ago' : `${String(minutes)} min ago`
  return `Showing numbers from ${age}. The gateway is slow right now.`
}

/**
 * The dashboard's team-wide stat row. Spend replaces the model page's
 * tokens/sec tile — it is the number a team lead opens the page for, and
 * the gateway prices it (`cost_usd`); the platform does no rate math.
 */
export function toTeamStats(
  summary: UsageSummaryResponse,
  current: UsageTimeseriesResponse,
): ServingStat[] {
  const points = current.points
  const prev = current.previous_points
  return [
    {
      label: 'Requests',
      value: formatCompact(summary.request_count),
      delta: deltaPct(
        sum(points, (p) => p.request_count),
        sum(prev, (p) => p.request_count),
      ),
      spark: spark(points, (p) => p.request_count),
      hint: null,
    },
    {
      label: 'Error rate',
      value: measured(
        summary.request_count,
        () => `${((summary.error_count / summary.request_count) * 100).toFixed(2)}%`,
      ),
      delta: measured(summary.request_count, () =>
        deltaPct(errorRate(points) * 100, errorRate(prev) * 100),
      ),
      spark: spark(points, (p) => (p.request_count === 0 ? 0 : p.error_count / p.request_count)),
      hint: null,
    },
    {
      label: 'Tokens',
      value: formatCompact(summary.total_tokens),
      delta: deltaPct(
        sum(points, (p) => p.total_tokens),
        sum(prev, (p) => p.total_tokens),
      ),
      spark: spark(points, (p) => p.total_tokens),
      hint: null,
    },
    {
      label: 'Latency p95',
      value: measured(
        summary.request_count,
        () =>
          `${Math.round(weightedMean(points, byRequests, (p) => p.p95_latency_ms)).toLocaleString()} ms`,
      ),
      delta: measured(summary.request_count, () =>
        deltaMs(
          weightedMean(points, byRequests, (p) => p.p95_latency_ms),
          weightedMean(prev, byRequests, (p) => p.p95_latency_ms),
        ),
      ),
      spark: spark(points, (p) => p.p95_latency_ms),
      hint: PERCENTILE_HINT,
    },
    {
      label: 'Spend',
      value:
        summary.pricing_status === 'unavailable' || summary.cost_usd === null
          ? '—'
          : `$${Number(summary.cost_usd).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
      delta:
        summary.pricing_status === 'unavailable'
          ? 'not priced'
          : !fullyPriced(prev)
            ? 'no prior price'
            : deltaPct(
                sum(points, (p) => Number(p.cost_usd ?? 0)),
                sum(prev, (p) => Number(p.cost_usd ?? 0)),
              ),
      spark: spark(points, (p) => Number(p.cost_usd ?? 0)),
      hint: null,
    },
  ]
}

/**
 * One matrix window. `burst` stays 0 in v1: splitting steady from burst
 * load needs a dimension the gateway does not record yet (`is_streaming`
 * or `route_via`), and the panel hides the burst series when it is empty.
 */
export function toRequestWindow(
  label: string,
  ts: UsageTimeseriesResponse,
): RequestWindow {
  const minutes = BUCKET_MINUTES[ts.bucket as UsageBucket]
  return {
    label,
    total: formatCompact(sum(ts.points, (p) => p.request_count)),
    buckets: ts.points.map((point) => ({
      label: bucketLabel(point.bucket_start, ts.bucket),
      detail: bucketDetail(point.bucket_start, ts.bucket),
      steady: point.request_count / minutes,
      burst: 0,
      requests: point.request_count,
      promptTokens: point.prompt_tokens,
      cachedPromptTokens: point.cached_prompt_tokens,
      completionTokens: point.completion_tokens,
      totalTokens: point.total_tokens,
    })),
  }
}

/**
 * One bucket of the tokens-per-second chart under the request matrix.
 *
 * Every rate is null when the bucket had no streamed request: there is
 * nothing to plot, and a 0 would draw the model stalling. The percentiles
 * are the gateway's, weighted by tokens, so p10 is the rate a tenth of the
 * bucket's output tokens were written at or below.
 */
export interface ThroughputBucket {
  /** The request matrix's axis and readout labels, so the two charts agree. */
  label: string
  detail: string
  avg: number | null
  p75: number | null
  p50: number | null
  p10: number | null
  p5: number | null
  p1: number | null
}

export function toThroughput(ts: UsageTimeseriesResponse): ThroughputBucket[] {
  return ts.points.map((point) => ({
    label: bucketLabel(point.bucket_start, ts.bucket),
    detail: bucketDetail(point.bucket_start, ts.bucket),
    avg: generationRate([point]),
    p75: point.p75_tokens_per_second,
    p50: point.p50_tokens_per_second,
    p10: point.p10_tokens_per_second,
    p5: point.p5_tokens_per_second,
    p1: point.p1_tokens_per_second,
  }))
}

/** One cell of the uptime wall, with everything its hover readout needs. */
export interface UptimeCell {
  level: UptimeLevel
  /** e.g. "Mon 03 · 8a-4p" */
  label: string
  /** Which models were unhealthy, and on whose side. Empty when clean. */
  impacts: UptimeImpact[]
  requests: number
  /** Requests in this block served by a fallback route (TRA-923). */
  fallbacks: number
  /**
   * How many consecutive incident blocks this one belongs to, counting
   * across day boundaries — 0 when the block is not an incident. An outage
   * that runs from Friday evening into Saturday is ONE outage, and reading
   * it off the wall means counting red squares by eye.
   */
  outageBlocks: number
  /**
   * When failures were OBSERVED — first and last failed probe across the
   * whole run of incident blocks this one belongs to, as ISO instants. Null
   * when the run has no probe timestamps (blocks predating the gateway's
   * span field), where only the block arithmetic (`outageBlocks × 8h`) is
   * known.
   *
   * A window, not a duration. Read `outageSeconds` for how long.
   */
  outageStart: string | null
  outageEnd: string | null
  /**
   * How long the run was actually down inside that window. Probes are
   * samples, so a wide window can hold minutes of downtime — the api caps
   * this at what the failed probes support (TRA-952). Null exactly when the
   * window is.
   */
  outageSeconds: number | null
}

const UPTIME_BLOCK_HOURS = ['12a-8a', '8a-4p', '4p-12a']

interface OutageRun {
  blocks: number
  start: string | null
  end: string | null
  /** Summed across the run's blocks; null when any block is span-less. */
  seconds: number | null
}

/**
 * Two failures this far apart are separate outages, not one long one.
 * Adjacent incident blocks only mean each block saw *some* failure — with
 * 8h blocks, a 4:18a blip and a 10:46a blip land in neighbouring blocks and
 * would otherwise read as one 6h32m outage the service never had. A genuine
 * outage crossing a block boundary has its last failure at one block's edge
 * and its next at the other's, a probe cadence (2 min) apart.
 *
 * Block granularity can't see a gap INSIDE one block, so this split only
 * catches gaps at block boundaries. The api caps a within-block span at
 * what its failed-probe COUNT supports before sending it (TRA-952,
 * usage_views.cap_span_end), which is the same correction by a different
 * route: two blips 7h apart in one block arrive here as minutes, not 7h.
 */
const OUTAGE_GAP_SPLIT_MINUTES = 30

/**
 * Incident runs over the flattened block sequence, keyed by flat index — an
 * outage does not stop at midnight. A run breaks when consecutive incident
 * blocks are separated by more than OUTAGE_GAP_SPLIT_MINUTES of green.
 *
 * Each run carries its exact span (the earliest and latest failed probe
 * across its blocks) ONLY when every block in the run has one: a run mixing
 * probed blocks with pre-cron or backfilled blocks (span-less by design)
 * would otherwise report a partial span and shrink a day-long outage to
 * minutes. Span-less blocks can't be gap-tested either, so they always
 * extend the run — the conservative direction, since that path already
 * refuses to report a span at all. ISO instants order lexicographically, so
 * string min/max is time min/max.
 */
function outageRuns(flat: UptimeResponse['days'][number]['blocks']): Map<number, OutageRun> {
  const runs = new Map<number, OutageRun>()
  let index = 0
  while (index < flat.length) {
    // Level 3 = incident. Fallback-active blocks (1) are NOT outages — a
    // block whose traffic the chain served must neither start nor extend
    // a run (TRA-923).
    if ((flat[index]?.level ?? 0) < 3) {
      index += 1
      continue
    }
    let end = index
    let first: string | null = null
    let last: string | null = null
    let seconds = 0
    let allSpanned = true
    while (end < flat.length && (flat[end]?.level ?? 0) >= 3) {
      const block = flat[end]
      // The api sends window and downtime together or not at all, so one
      // condition covers both. No `?? 0` here: a missing duration must fall
      // back to block arithmetic, never be charged as zero downtime.
      if (
        block?.first_failure == null ||
        block.last_failure == null ||
        block.outage_seconds == null
      ) {
        allSpanned = false
      } else {
        // `last` is the previous block's newest failure until we fold this
        // one in — the gap between them is the green stretch in between.
        // A span-less block in between makes that gap meaningless (it would
        // reach back past the untimed block and always split), so only test
        // while every block so far carried probe timestamps.
        if (last !== null && allSpanned) {
          const gap = new Date(block.first_failure).getTime() - new Date(last).getTime()
          if (gap > OUTAGE_GAP_SPLIT_MINUTES * 60_000) break
        }
        if (first === null || block.first_failure < first) first = block.first_failure
        if (last === null || block.last_failure > last) last = block.last_failure
        seconds += block.outage_seconds
      }
      end += 1
    }
    const run: OutageRun = {
      blocks: end - index,
      start: allSpanned ? first : null,
      end: allSpanned ? last : null,
      seconds: allSpanned ? seconds : null,
    }
    for (let i = index; i < end; i += 1) runs.set(i, run)
    index = end
  }
  return runs
}

export function toUptimeGrid(uptime: UptimeResponse): UptimeCell[][] {
  const runs = outageRuns(uptime.days.flatMap((day) => day.blocks))
  let cursor = -1
  return uptime.days.map((day) =>
    day.blocks.map((block, index) => {
      const run = runs.get((cursor += 1))
      return {
        // The wire types level as a plain number; the grid only knows four.
        level: (block.level >= 3
          ? 3
          : block.level === 2
            ? 2
            : block.level === 1
              ? 1
              : 0) as UptimeLevel,
        label: `${dayLabel(calendarDate(day.date))} · ${
          UPTIME_BLOCK_HOURS[index] ?? ''
        }`,
        impacts: block.impacts,
        requests: block.request_count,
        fallbacks: block.fallback_count,
        outageBlocks: run?.blocks ?? 0,
        outageStart: run?.start ?? null,
        outageEnd: run?.end ?? null,
        outageSeconds: run?.seconds ?? null,
      }
    }),
  )
}

/**
 * "11:42p" / "Aug 10 11:42p" — grid dialect, lowercase meridiem, in the
 * viewer's zone: the wall's row labels (12a–8a…) and day columns are local
 * block boundaries since TRA-918/919 (the gateway buckets in the viewer's
 * offset), so a UTC-rendered span would land outside the very row it sits
 * under. Composed by hand like `hourLabel`: Intl's en-US output separates
 * the meridiem with U+202F in modern ICU, so string-replacing " AM" is a
 * locale bug.
 */
function probeTime(iso: string, withDay: boolean): string {
  const date = new Date(iso)
  const hours = date.getHours()
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const time = `${hour12.toString()}:${minutes}${hours < 12 ? 'a' : 'p'}`
  if (!withDay) return time
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
  })
  return `${day} ${time}`
}

/**
 * "11:42p – 3:14a · 3h 32m down" — when a cell's run saw failed probes, and
 * how long it was down inside that window. Days appear when the window
 * crosses midnight. Null when the run predates the gateway's span field;
 * the caller falls back to block arithmetic.
 *
 * The window can be far wider than the downtime, because failed probes are
 * samples and sparse ones do not mean the gap between them was down
 * (TRA-952). Where inside the window the downtime sat is not knowable, so
 * the label states both facts rather than inventing a narrower window.
 */
export function outageSpanLabel(cell: UptimeCell): string | null {
  if (cell.outageStart === null || cell.outageEnd === null) return null
  if (cell.outageSeconds === null) return null
  const start = new Date(cell.outageStart)
  const end = new Date(cell.outageEnd)
  // Local dates, like the wall's day columns — see probeTime.
  const crossesDay = start.toDateString() !== end.toDateString()
  const minutes = Math.round(cell.outageSeconds / 60)
  // A single failed probe has no width; the probe cadence (2 min) is the
  // resolution floor, so claiming "0m" would overstate the precision.
  const duration =
    minutes < 2
      ? '<2m'
      : minutes < 60
        ? `${minutes.toString()}m`
        : `${Math.floor(minutes / 60).toString()}h ${(minutes % 60).toString()}m`
  return `${probeTime(cell.outageStart, crossesDay)} – ${probeTime(cell.outageEnd, crossesDay)} · ${duration} down`
}

/** Month labels under the grid, in order, one per month the range spans. */
export function uptimeMonthLabels(uptime: UptimeResponse): string[] {
  const seen: string[] = []
  for (const day of uptime.days) {
    const label = calendarDate(day.date).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    })
    if (seen[seen.length - 1] !== label) seen.push(label)
  }
  return seen
}

/** "Last 90 days in 8-hour blocks · 99.94% availability, 2 incidents" */
export function uptimeSummary(uptime: UptimeResponse): string {
  // The gateway zero-fills the whole 90-day grid, so a model deployed last
  // week arrives with 83 days of level-0 blocks from before it existed.
  // Counting those as "up" is how a route probed twice reports 100%
  // availability over a quarter it was not alive for. Measure from the
  // first block carrying any evidence — traffic or a non-zero level.
  //
  // A block that was probed clean and served nothing is indistinguishable
  // from one that never happened: the wire carries no probe count, only
  // failures. Until the gateway sends one, the honest read of an all-quiet
  // grid is that nothing was observed.
  const all = uptime.days.flatMap((day) => day.blocks)
  const first = all.findIndex((block) => block.level > 0 || block.request_count > 0)
  const blocks = first === -1 ? [] : all.slice(first)
  // No probes is NOT 100% availability. Reporting a perfect score from an
  // empty sample is the most confident possible way to say nothing, and it
  // reads as "we checked and it was fine" — which we did not.
  if (blocks.length === 0) return 'No availability recorded yet'
  // An incident is a RUN of blocks: a 24-hour outage is one incident, not
  // three. outageRuns is the ONE definition of a run — the same one the
  // wall's hover spans use — so the headline count and the grid can never
  // disagree about how many outages there were (a run's blocks all map to
  // the same object; distinct objects are distinct incidents).
  //
  // Availability is downtime over observed time, from each run's measured
  // downtime — a 2-minute blip is 2 minutes down, not a whole 8h block
  // (TRA-920: three short incidents read as 96.84% when the true figure
  // was 99.6%). A single failed probe has no span width; the probe cadence
  // (2 min) is the resolution floor, same as outageSpanLabel's "<2m".
  //
  // The run's DOWNTIME, not the width of its window (TRA-952). The two
  // differ whenever probes failed sparsely, and the window is the wrong
  // one of the pair: it charges every green minute between two unlucky
  // probes as an outage. It also silently included the green gaps BETWEEN
  // blocks inside one run, which is the same bug at run scale.
  // A span-less run (pre-cron history or statuspage backfill) marks the
  // un-probed era: charging it 8h per blip drags a 2-minute pre-cron blip
  // back to a 1% availability hit — the exact distortion TRA-920 removed
  // for probed incidents. Since span-less runs only exist before probing
  // began, start the measured window after the last one — the same move
  // as the first-evidence trim above, for the same reason: measure only
  // where we could measure. A spanned block adjacent to a span-less one
  // is folded into the same (span-less) run by outageRuns, so this cut
  // always lands on a run boundary and can never bisect an incident.
  //
  // The trim moves ONLY the percentage's window. The incident count stays
  // the whole wall's — every red square the grid draws is a counted
  // incident, or the headline reads "0 incidents" under a visible red run
  // and looks broken. "100% availability, 3 incidents" is coherent: the
  // incidents predate the span the percentage names.
  //
  // A model whose ONLY history is span-less would trim to nothing, and
  // 100% over an empty tail is the empty-sample lie this function already
  // refuses — keep the full window there; block arithmetic is the only
  // honest read of an un-probed past.
  const BLOCK_MS = 8 * 3_600_000
  const allRuns = outageRuns(blocks)
  let cut = 0
  for (const [index, run] of allRuns) {
    if (run.start === null && index >= cut) cut = index + 1
  }
  const trimmed = cut < blocks.length ? cut : 0
  const incidents = new Set(allRuns.values()).size
  // Runs never straddle the cut (it lands after a whole run), so a run
  // with any block index past the trim lies entirely inside the measured
  // window. Runs before it — span-less by construction, plus any spanned
  // run interleaved with backfill — are outside the measured time and
  // cost nothing, exactly as their time contributes nothing.
  const measuredRuns = new Set(
    [...allRuns.entries()]
      .filter(([index]) => index >= trimmed)
      .map(([, run]) => run),
  )
  let downMs = 0
  for (const run of measuredRuns) {
    downMs +=
      run.seconds !== null ? Math.max(run.seconds * 1000, 2 * 60_000) : run.blocks * BLOCK_MS
  }
  const observedMs = (blocks.length - trimmed) * BLOCK_MS
  const availability = ((observedMs - downMs) / observedMs) * 100
  const plural = incidents === 1 ? 'incident' : 'incidents'
  // Name the span the percentage was actually measured over, not the span
  // the wall draws — they differ for anything younger than the grid.
  const observed = Math.ceil((blocks.length - trimmed) / 3)
  return `Last ${observed.toString()} days in 8-hour blocks · ${availability.toFixed(2)}% availability, ${incidents.toLocaleString()} ${plural}`
}

/**
 * One cell of the request-log CSV export.
 *
 * `error_message` and `request_model` are upstream-controlled strings, and a
 * cell starting with = + - @ (or a leading tab/CR, which spreadsheets strip
 * before parsing) is read as a FORMULA by Excel and Sheets. Quoting does NOT
 * neutralise that — the leading apostrophe does — so escape before quoting.
 */
export function csvCell(value: string | number | null | undefined): string {
  if (value == null) return ''
  let text = String(value)
  // \n belongs here alongside \r and \t: the quoting branch below keeps a
  // leading newline inside the cell, and spreadsheets skip it as whitespace
  // before evaluating what follows — so `\n=HYPERLINK(...)` still runs.
  if (/^[=+\-@\t\r\n]/.test(text)) text = `'${text}`
  // \r forces quoting as much as \n does: a bare CR is a record boundary to
  // most CSV readers, so `failed\r=HYPERLINK(...)` would split into a second
  // row whose first cell starts with `=` — past the leading-char guard, which
  // only ever sees position 0.
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}
